import { useEffect, useRef, useState } from 'react';

interface UseScrollRevealOptions {
  threshold?: number;
  rootMargin?: string;
  once?: boolean;
  staggerDelay?: number; // ms between elements that enter the viewport together
  childSelector?: string;
}

/**
 * Reveals every `.reveal` element inside a container as it scrolls into view,
 * adding `.is-visible`. Elements entering together are staggered.
 * Children can override the delay with `data-delay="0.15"` (seconds) or
 * `data-delay="150"` (milliseconds). Elements added later (data loaded,
 * filters changed) are picked up automatically, so nothing stays hidden.
 */
export function useScrollReveal<T extends HTMLElement = HTMLDivElement>({
  threshold = 0.1,
  rootMargin = '0px 0px -40px 0px',
  once = true,
  staggerDelay = 90,
  childSelector = '.reveal',
}: UseScrollRevealOptions = {}) {
  const containerRef = useRef<T>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const show = (el: HTMLElement, batchIndex: number) => {
      const customDelay = el.dataset.delay;
      const parsedDelay = customDelay ? parseFloat(customDelay) : NaN;
      const delayMs = Number.isFinite(parsedDelay)
        ? (parsedDelay <= 1 ? parsedDelay * 1000 : parsedDelay)
        : batchIndex * staggerDelay;
      el.style.transitionDelay = `${Math.min(delayMs, 600)}ms`;
      el.classList.add('is-visible');
    };

    const childObserver = new IntersectionObserver(
      (entries) => {
        let batch = 0;
        entries.forEach((entry) => {
          const el = entry.target as HTMLElement;
          // Content that is already above the viewport (inserted after the visitor
          // scrolled past, or jumped over via an anchor) is shown at once, never left hidden.
          const passed = once && entry.boundingClientRect.bottom <= (entry.rootBounds?.top ?? 0);
          if (entry.isIntersecting || passed) {
            show(el, passed ? 0 : batch++);
            if (once) childObserver.unobserve(el);
          } else if (!once) {
            el.classList.remove('is-visible');
          }
        });
      },
      { threshold: Math.min(threshold, 0.15), rootMargin }
    );

    const track = (root: HTMLElement) => {
      root.querySelectorAll<HTMLElement>(childSelector).forEach((el) => {
        if (!el.classList.contains('is-visible')) childObserver.observe(el);
      });
    };
    track(container);
    if (container.matches(childSelector)) childObserver.observe(container);

    const mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((m) => m.addedNodes.forEach((node) => {
        if (!(node instanceof HTMLElement)) return;
        if (node.matches(childSelector)) childObserver.observe(node);
        track(node);
      }));
    });
    mutationObserver.observe(container, { childList: true, subtree: true });

    const containerObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          if (once) containerObserver.disconnect();
        } else if (!once) {
          setVisible(false);
        }
      },
      { threshold, rootMargin }
    );
    containerObserver.observe(container);

    return () => {
      childObserver.disconnect();
      mutationObserver.disconnect();
      containerObserver.disconnect();
    };
  }, [threshold, rootMargin, once, staggerDelay, childSelector]);

  return { containerRef, ref: containerRef, className: visible ? 'is-visible' : '', visible };
}

/**
 * Simpler variant for a single element (no stagger).
 */
export function useSingleReveal(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);

  return { ref, isVisible };
}
